import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import Login from "./Login";
import "./App.css";

function App() {
  const [token, setToken] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [history, setHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const historyEndRef = useRef(null);

  const IDLE_TIMEOUT_MINUTES = 15;
  let idleTimer;

  // Automatski logout ako korisnik ne koristi aplikaciju duže od IDLE_TIMEOUT_MINUTES
  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        console.log("User inactive. Logging out.");
        logout();
      }, IDLE_TIMEOUT_MINUTES * 60 * 1000);
    };

    const events = ["mousemove", "keydown", "click"];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(idleTimer);
    };
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      setToken(savedToken);
      fetchSessions(savedToken);
    }
  }, []);

  const logout = () => {
    console.log("Logging out");
    localStorage.removeItem("token");
    setToken(null);
    setSessionId(null);
    setHistory([]);
    setAnswer("");
    setSessions([]);
  };

  const fetchSessions = async (token) => {
    try {
      const res = await fetch("https://happy-meadow-f91da3c9f1f046f09b85c5675deb93bc.azurewebsites.net/sessions", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      const data = await res.json();
      setSessions(data);
      if (data.length > 0) {
        setSessionId(data[data.length - 1].session_id);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    }
  };

  useEffect(() => {
    if (!token || !sessionId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch("https://happy-meadow-f91da3c9f1f046f09b85c5675deb93bc.azurewebsites.net/history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          logout();
          return;
        }
        const data = await res.json();
        const selectedSession = data.sessions.find(
          (s) => s.session_id === sessionId
        );
        setHistory(selectedSession?.messages || []);
        setAnswer("");
      } catch (err) {
        console.error("Error fetching history:", err);
      }
    };
    fetchHistory();
  }, [sessionId, token]);

  const createSession = async (token) => {
    try {
      const res = await fetch("https://happy-meadow-f91da3c9f1f046f09b85c5675deb93bc.azurewebsites.net/session/new", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        fetchSessions(token);
        setAnswer("");
        setQuestion("");
        setHistory([]);
      } else {
        console.error("Failed to create session");
      }
    } catch (err) {
      console.error("Session error:", err);
    }
  };

  const streamQuestion = async () => {
    if (!question.trim() || !sessionId || !token) return;

    setIsTyping(true);
    const encodedQuestion = encodeURIComponent(question);
    const url = `https://happy-meadow-f91da3c9f1f046f09b85c5675deb93bc.azurewebsites.net/ask/stream?session_id=${sessionId}&question=${encodedQuestion}&token=${token}`;

    const updatedHistory = [...history, { role: "user", content: question }];
    setHistory(updatedHistory);
    setQuestion("");

    try {
      const response = await fetch(url);
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok || !response.body) {
        throw new Error("Failed to stream answer");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let streamedAnswer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        streamedAnswer += chunk.replace(/^data:\s?/gm, "").replace(/\n\n/g, "");
        setAnswer(streamedAnswer);
      }

      const updated = [
        ...updatedHistory,
        { role: "assistant", content: streamedAnswer },
      ];
      setHistory(updated);
    } catch (error) {
      console.error("Streaming failed:", error);
    } finally {
      setIsTyping(false);
    }
  };

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, isTyping]);

  useEffect(() => {
  window.scrollTo(0, 0);
}, []);

  if (!token)
    return (
      <Login
        onLogin={(t) => {
          setToken(t);
          localStorage.setItem("token", t);
          fetchSessions(t);
        }}
      />
    );

  return (
    <div className="App">
      <div className="sidebar">
        <h3>Sessions</h3>
          <button className="new-session-button" onClick={() => createSession(token)}>New Session</button>
        <ul>
          {sessions.map((s) => (
            <li
              key={s.session_id}
              className={s.session_id === sessionId ? "active" : ""}
              onClick={() => setSessionId(s.session_id)}
            >
              {s.title}
            </li>
          ))}
        </ul>
      </div>

      <div className="main">
        <h1 className="ai-agent-header">mISTiQ</h1>
        <div style={{ marginBottom: "10px" }}>
          {sessionId && (
            <div style={{ marginTop: "8px", fontSize: "small" }}>
              Session ID: <code>{sessionId}</code>
            </div>
          )}
        </div>

        <div className="history chat-scroll">
          {history.map((msg, idx) => (
            <div key={idx} className={msg.role}>
              <strong>{msg.role === "user" ? "You:" : "Agent:"}</strong>
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => (
                    <p className="message" {...props} />
                  ),
                  pre: ({ node, ...props }) => (
                    <pre className="message" {...props} />
                  ),
                  code: ({ node, ...props }) => (
                    <code className="message" {...props} />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote className="message" {...props} />
                  ),
                }}
              >
                {msg.content}
              </ReactMarkdown>
            </div>
          ))}
          {isTyping && <div className="typing-indicator">Typing...</div>}
          <div ref={historyEndRef} />
        </div>

        <div className="chatbox">
          <textarea
            placeholder="Ask something about IST metadata..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
          />
          <button onClick={streamQuestion}>Ask</button>
        </div>
      </div>

          <button className="logout-button" onClick={logout}>
            Logout
          </button>

    </div>
  );
}

export default App;
